<template>
  <div v-if="show" :class="`months-grid months-grid--${size}`" @click.stop>
    <div
      v-for="(monthName, index) in monthNames"
      :key="index"
      :class="clsx('month-item', {
          'current-month': index === getMonthMoment(currentDate) && selectedYear === getYearMoment(currentDate),
          'today-month': isCurrentMonthToday(index),
          'selected': isSelectedMonth(index),
          'in-range': isInMonthRange(index),
          'range-start': isMonthRangeStart(index),
          'range-end': isMonthRangeEnd(index),
          'is-disabled': isDisabledMonth(index)
        })"
      @click="selectMonth(index)"
      :aria-label="`Select ${monthName}`"
    >
      {{ monthName }}
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';
import { moment } from '../../lib';

export default {
  props: {
    /**
     * Controls the visibility of the month grid.
     * @type {boolean}
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * Defines the size of the month grid.
     * @type {string}
     * @values 'normal', 'small'
     */
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value)
    },
    /**
     * An array of month names to display in the grid.
     * (e.g., ['Січень', 'Лютий', ...])
     * @type {Array<string>}
     * @required
     */
    monthNames: {
      type: Array,
      required: true,
    },
    /**
     * The current date object (Moment.js or Date) from the parent calendar.
     * Used to determine the 'current-month' highlight.
     * @type {object}
     * @required
     */
    currentDate: {
      type: Object,
      required: true,
    },
    /**
     * The year currently selected in the calendar.
     * Used to determine if a month is 'today-month' within the context of this year.
     * @type {number}
     * @required
     */
    selectedYear: {
      type: Number,
      required: true,
    },
    /**
     * The mode of the calendar. Used to determine selection behavior.
     * @type {string}
     * @values 'date-selection', 'month-selection'
     */
    mode: {
      type: String,
      default: 'date-selection',
      validator: (value) => ['date-selection', 'month-selection'].includes(value)
    },
    /**
     * The start date of a selected range (Moment.js object).
     * Only relevant when `mode` is 'month-selection' and `range` is true.
     * @type {object}
     */
    startDate: {
      type: Object,
      default: null,
    },
    /**
     * The end date of a selected range (Moment.js object).
     * Only relevant when `mode` is 'month-selection' and `range` is true.
     * @type {object}
     */
    endDate: {
      type: Object,
      default: null,
    },
    /**
     * If true, the calendar is in range selection mode.
     * @type {boolean}
     */
    range: {
      type: Boolean,
      default: false,
    },
    /**
     * The maximum number of units (days or months) allowed in a selected range (inclusive).
     * If null, there is no limit.
     * @type {number | null}
     */
    maxRangeLimit: {
      type: Number,
      default: null,
    },
    /**
     * The unit for the maxRangeLimit ('day' or 'month').
     * @type {string}
     * @values 'day', 'week', 'month'
     */
    limitUnit: {
      type: String,
      default: 'month',
      validator: (value) => ['day', 'week', 'month'].includes(value),
    },
    /**
     * Internal state indicating if a range selection is currently in progress (startDate set, endDate not).
     * @type {boolean}
     */
    selectingRange: {
      type: Boolean,
      default: false,
    },
    availableDates: {
      type: Array,
      default: () => [],
      validator: (value) => value?.every((item) => {
        return item && (typeof item === 'string' || item instanceof Date || (typeof item === 'object' && item.date));
      }),
    },
  },
  mixins: [ moment ],
  methods: {
    clsx,
    /**
     * Emits a 'select-month' event with the index of the selected month.
     * @param {number} monthIndex - The 0-indexed month (0 for January, 11 for December).
     */
    selectMonth(monthIndex) {
      if (!this.isDisabledMonth(monthIndex)) {
        this.$emit('select-month', monthIndex);
      }
    },
    /**
     * Checks if the given month index corresponds to the current month of today's date
     * within the context of the currently selected year.
     * @param {number} monthIndex - The 0-indexed month to check.
     * @returns {boolean} True if it's today's month in the selected year, false otherwise.
     */
    isCurrentMonthToday(monthIndex) {
      const today = this.getMoment();
      return (
        monthIndex === this.getMonthMoment(today) &&
        this.selectedYear === this.getYearMoment(today)
      );
    },
    /**
     * Checks if the given month index is the currently selected month.
     * Relevant when the calendar is in 'month-selection' mode and not in range mode.
     * @param {number} monthIndex - The 0-indexed month to check.
     * @returns {boolean} True if it's the selected month, false otherwise.
     */
    isSelectedMonth(monthIndex) {
      if (this.mode === 'month-selection' && !this.range && this.currentDate) {
        return monthIndex === this.getMonthMoment(this.currentDate) &&
          this.selectedYear === this.getYearMoment(this.currentDate);
      }
      return false;
    },
    /**
     * Creates a Moment.js object for a given month index and the current selected year.
     * @param {number} monthIndex - The 0-indexed month.
     * @returns {object} A Moment.js object representing the start of that month.
     */
    getMomentForMonth(monthIndex) {
      const currentYear = this.selectedYear;
      let m = this.getMoment();
      m = this.setYearMoment(m, currentYear);
      m = this.setMonthMoment(m, monthIndex);

      return m.startOf('month');
    },
    /**
     * Calculates the number of months between two Moment.js objects (inclusive).
     * @param {object} startMoment - The start Moment.js object.
     * @param {object} endMoment - The end Moment.js object.
     * @returns {number} The number of months in the range (e.g., Jan-Jan is 1 month).
     */
    getMonthsDifference(startMoment, endMoment) {
      const start = this.cloneMoment(startMoment).startOf('month');
      const end = this.cloneMoment(endMoment).startOf('month');

      if (window.moment) {
        return Math.abs(start.diff(end, 'months')) + 1;
      }

      const yearsDiff = this.getYearMoment(end) - this.getYearMoment(start);
      const monthsDiff = this.getMonthMoment(end) - this.getMonthMoment(start);

      return (yearsDiff * 12) + monthsDiff + 1;
    },
    /**
     * Calculates the number of days between two Moment.js objects (inclusive).
     * @param {object} startMoment - The start Moment.js object.
     * @param {object} endMoment - The end Moment.js object.
     * @returns {number} The number of days in the range (e.g., Jan 1 - Jan 1 is 1 day).
     */
    getDaysDifference(startMoment, endMoment) {
      return this._getDaysDifference(startMoment, endMoment);
    },
    /**
     * Calculates the number of weeks between two Moment.js objects or dates (inclusive).
     * This method relies on momentMixin methods being available via 'this'.
     * @param {object} startMoment - The start Moment.js object or Date object.
     * @param {object} endMoment - The end Moment.js object or Date object.
     * @returns {number} The number of weeks in the range (e.g., Week 1 - Week 1 is 1 week).
     */
    getWeeksDifference(startMoment, endMoment) {
      const m1 = this.startOfMoment(this.getMoment(startMoment), 'isoWeek');
      const m2 = this.startOfMoment(this.getMoment(endMoment), 'isoWeek');

      if (window.moment) {
        return Math.abs(m1.diff(m2, 'weeks')) + 1;
      }

      const d1 = this.toDateMoment(m1);
      const d2 = this.toDateMoment(m2);
      const diffTime = Math.abs(d2.getTime() - d1.getTime());
      const diffWeeks = Math.floor(diffTime / (1000 * 60 * 60 * 24 * 7));

      return diffWeeks + 1;
    },
    /**
     * Checks if a month is within the selected month range.
     * @param {number} monthIndex - The 0-indexed month to check.
     * @returns {boolean} True if the month is in range, false otherwise.
     */
    isInMonthRange(monthIndex) {
      if (!this.range || !this.startDate || this.selectingRange) {
        return false;
      }

      const isEndDateValidAndPresent = (window.moment && this.endDate)
        ? (window.moment.isMoment(this.endDate) && this.endDate.isValid())
        : (this.endDate instanceof Date && !isNaN(this.endDate.getTime()));

      if (!isEndDateValidAndPresent) {
        return false;
      }

      const monthMoment = this.getMomentForMonth(monthIndex);
      return this.isSameOrAfterMoment(monthMoment, this.startDate, 'month') &&
        this.isSameOrBeforeMoment(monthMoment, this.endDate, 'month');
    },
    /**
     * Checks if a month is the start of the selected month range.
     * @param {number} monthIndex - The 0-indexed month to check.
     * @returns {boolean} True if it's the range start, false otherwise.
     */
    isMonthRangeStart(monthIndex) {
      if (this.mode === 'month-selection' && this.range && this.startDate) {
        const currentMonthMoment = this.getMomentForMonth(monthIndex);
        return this.isSameMoment(currentMonthMoment, this.startDate, 'month');
      }
      return false;
    },
    /**
     * Checks if a month is the end of the selected month range.
     * @param {number} monthIndex - The 0-indexed month to check.
     * @returns {boolean} True if it's the range end, false otherwise.
     */
    isMonthRangeEnd(monthIndex) {
      if (this.mode === 'month-selection' && this.range && this.endDate) {
        const currentMonthMoment = this.getMomentForMonth(monthIndex);
        return this.isSameMoment(currentMonthMoment, this.endDate, 'month');
      }
      return false;
    },
    isMonthAvailable(monthIndex) {
      if (this.availableDates.length === 0) {
        return true;
      }

      return this.availableDates.some((availableDateItem) => {
        const comparableDate = availableDateItem.date || availableDateItem;
        const dateMoment = this.getMoment(comparableDate);

        return this.getMonthMoment(dateMoment) === monthIndex &&
          this.getYearMoment(dateMoment) === this.selectedYear;
      });
    },
    isDisabledMonth(monthIndex) {
      if (!this.isMonthAvailable(monthIndex)) {
        return true;
      }

      if (!this.range || this.maxRangeLimit === null || !this.startDate) {
        return false;
      }

      const startMoment = this.getMoment(this.startDate);
      const monthMoment = this.getMomentForMonth(monthIndex);

      if (this.selectingRange && this.limitUnit === 'day') {
        const minAllowedDate = this.subtractMoment(this.cloneMoment(startMoment), this.maxRangeLimit - 1, 'days');
        const maxAllowedDate = this.addMoment(this.cloneMoment(startMoment), this.maxRangeLimit - 1, 'days');
        const monthStart = this.startOfMoment(monthMoment, 'month');
        const monthEnd = this.endOfMoment(monthMoment, 'month');

        return this.isAfterMoment(monthStart, maxAllowedDate, 'day') || this.isBeforeMoment(monthEnd, minAllowedDate, 'day');
      }

      if (this.selectingRange) {
        let potentialRangeStart;
        let potentialRangeEnd;

        if (this.isSameOrBeforeMoment(monthMoment, startMoment, 'month')) {
          potentialRangeStart = this.startOfMoment(monthMoment, 'month');
          potentialRangeEnd = startMoment;
        } else {
          potentialRangeStart = startMoment;
          potentialRangeEnd = this.endOfMoment(monthMoment, 'month');
        }

        let calculatedDifference;
        switch (this.limitUnit) {
          case 'month':
            calculatedDifference = this.getMonthsDifference(potentialRangeStart, potentialRangeEnd);
            break;
          case 'week':
            calculatedDifference = this.getWeeksDifference(potentialRangeStart, potentialRangeEnd);
            break;
          default:
            calculatedDifference = this.getDaysDifference(potentialRangeStart, potentialRangeEnd);
        }

        if (calculatedDifference > this.maxRangeLimit) {
          return true;
        }
      }

      return false;
    },

    _getDaysDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);
      const diffTime = Math.abs(d2.getTime() - d1.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1;
    },
  },
};
</script>

<style lang="scss" scoped>
$small-scale-factor: 0.8;
$months-years-grid-item-font-size: 18px;

.months-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background-color: #fff;

  &--small {
    .month-item {
      font-size: $months-years-grid-item-font-size * $small-scale-factor;
    }
  }
}

.month-item {
  font-family: inherit;
  font-size: $months-years-grid-item-font-size;
  font-weight: 400;
  text-align: center;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.1s ease;

  &:hover {
    background-color: #ededed;
  }

  &:active {
    background-color: #e0e0e0;
    transform: scale(0.98);
  }

  &.today-month {
    background-color: #fb923c;
    color: white;
    font-weight: 600;
  }

  &.selected {
    background-color: #EDEDED;
    color: #09090B;
    font-weight: 500;
    &:hover {
      background-color: darken(#EDEDED, 5%);
    }
  }

  &.in-range {
    background-color: #EDEDED;
    font-weight: 500;
    color: inherit;
    &:hover {
      background-color: darken(#EDEDED, 5%);
    }
  }

  &.range-start,
  &.range-end {
    font-weight: 600;
  }

  &.is-disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.3;
    background-color: #FCE4E4;
    color: #999;

    &:hover {
      background-color: #FCE4E4;
    }
    &:active {
      transform: none;
    }
  }
}
</style>
